import api from './client';
import { API_ENDPOINTS } from './endpoints';

export type SupportIssuePriority = 'low' | 'medium' | 'high' | 'critical';
export type SupportIssueStatus = 'open' | 'in_progress' | 'on_hold' | 'resolved' | 'closed';

export interface SupportIssueApiItem {
  _id: string;
  support_issue_id?: string;
  custom_support_id?: string;
  issue_id?: string;
  title?: string;
  description?: string;
  raised_by?: string;
  raised_by_name?: string;
  raised_by_employee_code?: string;
  department_name?: string;
  priority?: SupportIssuePriority | string;
  status?: SupportIssueStatus | string;
  action_remarks?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportIssueListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    issues?: SupportIssueApiItem[];
    supportIssues?: SupportIssueApiItem[];
    items?: SupportIssueApiItem[];
    records?: SupportIssueApiItem[];
    count?: number;
  } | SupportIssueApiItem[];
}

export interface SupportIssueMutationResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: SupportIssueApiItem;
}

export interface CreateSupportIssuePayload {
  title: string;
  description: string;
  raised_by?: string;
  department_name?: string;
  priority: SupportIssuePriority;
}

export interface UpdateSupportIssuePayload extends CreateSupportIssuePayload {}

export interface SupportIssueActionPayload {
  action: string;
  status?: SupportIssueStatus;
  remarks?: string;
}

export interface GetSupportIssuesParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: SupportIssueStatus;
  priority?: SupportIssuePriority;
  issue_type?: string;
  department_name?: string;
  assigned_to_id?: string;
  [key: string]: string | number | boolean | undefined;
}

export const getSupportIssues = async (params?: GetSupportIssuesParams): Promise<SupportIssueListResponse> => {
  const queryParams: GetSupportIssuesParams = {
    ...params,
    search: params?.search?.trim() || undefined,
  };

  return api.get<SupportIssueListResponse>(API_ENDPOINTS.support.list, { params: queryParams });
};

export const getSupportIssueById = async (id: string): Promise<SupportIssueMutationResponse> => {
  return api.get<SupportIssueMutationResponse>(`${API_ENDPOINTS.support.list}/${id}`);
};

export const createSupportIssue = async (payload: CreateSupportIssuePayload): Promise<SupportIssueMutationResponse> => {
  return api.post<SupportIssueMutationResponse, CreateSupportIssuePayload>(API_ENDPOINTS.support.list, payload);
};

export const updateSupportIssue = async (
  id: string,
  payload: UpdateSupportIssuePayload,
): Promise<SupportIssueMutationResponse> => {
  return api.put<SupportIssueMutationResponse, UpdateSupportIssuePayload>(`${API_ENDPOINTS.support.list}/${id}`, payload);
};

export const deleteSupportIssue = async (id: string): Promise<SupportIssueMutationResponse> => {
  return api.delete<SupportIssueMutationResponse>(`${API_ENDPOINTS.support.list}/${id}`);
};

export const takeSupportIssueAction = async (
  id: string,
  payload: SupportIssueActionPayload,
): Promise<SupportIssueMutationResponse> => {
  return api.post<SupportIssueMutationResponse, SupportIssueActionPayload>(`${API_ENDPOINTS.support.list}/${id}/action`, payload);
};
